import type { AvailableTransition } from '@servicetim/shared';
import {
  useExecuteTransition,
  useIssueTypes,
  useSetTicketStatus,
  useTicketTransitions,
} from '@servicetim/ui/api';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  describe,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { ArrowRight, ChevronDown, LoaderCircle, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { StatusBadge } from './badges';
import { useTicketContext } from './ticket-context';
import { TransitionDialog } from './transition-dialog';

/**
 * Available transitions as buttons (S-05-04): a transition with a form opens the dialog,
 * otherwise it runs at once. Project admins get a "Set status" override (S-05-06).
 */
export function TransitionsBar() {
  const { ticket, canAdmin, status } = useTicketContext();
  const transitions = useTicketTransitions(ticket.canTransition ? ticket.id : undefined);
  const execute = useExecuteTransition();
  const [dialog, setDialog] = useState<AvailableTransition | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [runningId, setRunningId] = useState<string | null>(null);

  const run = async (t: AvailableTransition) => {
    if (t.formId) {
      setDialog(t);
      return;
    }
    setError(null);
    setRunningId(t.id);
    try {
      await execute.mutateAsync({ id: ticket.id, transitionId: t.id, fields: {} });
      toast.success(`${ticket.key}: ${t.name}`);
    } catch (e) {
      setError(e);
    } finally {
      setRunningId(null);
    }
  };

  const items = transitions.data ?? [];
  return (
    <section aria-label="Transitions" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge
          label={ticket.display.status_id}
          category={status?.category}
          color={status?.color}
          className="text-sm"
        />
        {ticket.canTransition ? (
          <>
            {transitions.isPending ? (
              <LoaderCircle
                className="size-4 animate-spin text-fg-muted"
                aria-label="Loading transitions"
              />
            ) : null}
            {items.length > 0 || canAdmin ? (
              <fieldset
                aria-label="Status actions"
                className="inline-flex flex-wrap items-center gap-1 rounded-md border border-border bg-surface-sunken p-1"
              >
                {items.map((t) => (
                  <Button
                    key={t.id}
                    size="sm"
                    variant="outline"
                    onClick={() => void run(t)}
                    disabled={runningId !== null}
                    aria-busy={runningId === t.id || undefined}
                  >
                    {runningId === t.id ? (
                      <LoaderCircle className="animate-spin" />
                    ) : (
                      <ArrowRight />
                    )}
                    {t.name}
                  </Button>
                ))}
                {canAdmin ? <SetStatusMenu /> : null}
              </fieldset>
            ) : null}
            {transitions.isSuccess && items.length === 0 && !canAdmin ? (
              <span className="text-fg-muted text-sm">No transitions available from here.</span>
            ) : null}
          </>
        ) : (
          <span className="text-fg-muted text-sm">You cannot transition this ticket.</span>
        )}
      </div>
      {transitions.error ? <ProblemBanner error={transitions.error} size="sm" /> : null}
      {error ? <ProblemBanner error={error} size="sm" /> : null}
      <TransitionDialog
        ticket={ticket}
        transition={dialog}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
      />
    </section>
  );
}

/** Admin override: any status of the ticket's published workflow, without rules (S-05-06). */
function SetStatusMenu() {
  const { ticket, statuses } = useTicketContext();
  const issueTypes = useIssueTypes(ticket.projectId);
  const setStatus = useSetTicketStatus();
  const workflowId = issueTypes.data?.find((t) => t.id === ticket.issueTypeId)?.workflowId;
  const options = statuses.statuses.filter(
    (s) => (workflowId === undefined || s.workflowId === workflowId) && s.id !== ticket.statusId,
  );
  const move = async (statusId: string) => {
    try {
      await setStatus.mutateAsync({ id: ticket.id, statusId });
      toast.success('Status set');
    } catch (e) {
      const view = describe(e);
      toast.error(view.title, { description: view.detail ?? undefined });
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" disabled={setStatus.isPending || options.length === 0}>
          <ShieldCheck />
          Set status
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Admin override (no rules run)</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((s) => (
          <DropdownMenuItem key={s.id} onSelect={() => void move(s.id)}>
            <span
              aria-hidden="true"
              className="size-2 rounded-full"
              style={{ backgroundColor: s.color ?? '#94a3b8' }}
            />
            {s.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
